import { AbstractConnector } from '@web3-react/abstract-connector'
import EthereumProvider from '@walletconnect/ethereum-provider'

const WC_OPTIONS = {
  projectId: process.env.REACT_APP_WALLETCONNECT_PROJECT_ID!,
  chains: [1] as [number, ...number[]],
  showQrModal: true,
}

// Module-level singleton shared with getProvider.ts via getWCProvider()
let _provider: InstanceType<typeof EthereumProvider> | null = null

export function getWCProvider(): InstanceType<typeof EthereumProvider> | null {
  return _provider
}

export class WalletConnectV2Connector extends AbstractConnector {
  private isActivating = false
  private _disconnectHandler: (() => void) | null = null

  constructor() {
    super({}) // no supportedChainIds — all chains allowed; app validates chains itself
  }

  async activate() {
    if (this.isActivating) return
    this.isActivating = true

    try {
      if (!_provider) {
        _provider = await EthereumProvider.init(WC_OPTIONS)
      }

      if (!_provider.session) {
        await _provider.connect()
      }

      // Register disconnect listener on BOTH fresh-connect and session-restore paths
      this._disconnectHandler = () => this.emitDeactivate()
      _provider.on('disconnect', this._disconnectHandler)

      return {
        provider: _provider,
        account: _provider.accounts[0] ?? null,
        chainId: _provider.chainId,
      }
    } finally {
      this.isActivating = false
    }
  }

  async getProvider() {
    return _provider
  }

  async getChainId() {
    return _provider?.chainId ?? null
  }

  async getAccount() {
    return _provider?.accounts[0] ?? null
  }

  deactivate() {
    if (_provider && this._disconnectHandler) {
      _provider.removeListener('disconnect', this._disconnectHandler)
      this._disconnectHandler = null
    }

    const p = _provider
    _provider = null

    if (p) {
      p.disconnect().catch(() => {
        // swallow — provider may already be disconnected
      })
    }
  }

  async isAuthorized() {
    // Not used by this app's useEagerConnect (which calls login() directly)
    return false
  }
}

export const walletConnectConnector = new WalletConnectV2Connector()
